import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { centralDbRequest } from '../services/centralDbClient';

type OpenApiSpec = any;

type OperationItem = {
  id: string;
  method: string;
  path: string;
  summary?: string;
  tags: string[];
  operation: any;
};

const METHOD_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  GET: 'info',
  POST: 'success',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
};

const safeJsonParse = (value: string): { ok: true; data: any } | { ok: false; error: string } => {
  try {
    return { ok: true, data: JSON.parse(value) };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Invalid JSON' };
  }
};

const schemaToExample = (schema: any, depth = 0): any => {
  if (!schema || depth > 4) return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length) return schema.enum[0];

  const t = schema.type;
  if (t === 'string') return schema.format === 'date-time' ? new Date().toISOString() : 'string';
  if (t === 'number' || t === 'integer') return 0;
  if (t === 'boolean') return false;
  if (t === 'array') return [schemaToExample(schema.items, depth + 1)];
  if (t === 'object' || schema.properties) {
    const out: any = {};
    const props = schema.properties || {};
    for (const [k, v] of Object.entries(props)) {
      out[k] = schemaToExample(v, depth + 1);
    }
    return out;
  }
  if (schema.oneOf?.length) return schemaToExample(schema.oneOf[0], depth + 1);
  if (schema.anyOf?.length) return schemaToExample(schema.anyOf[0], depth + 1);
  if (schema.allOf?.length) return schemaToExample(schema.allOf[0], depth + 1);
  return null;
};

const extractJsonSchema = (operation: any): any | null => {
  const content = operation?.requestBody?.content;
  if (!content) return null;
  if (content['application/json']?.schema) return content['application/json'].schema;
  const first = Object.values(content)[0] as any;
  return first?.schema || null;
};

const listOperations = (spec: OpenApiSpec): OperationItem[] => {
  const paths = spec?.paths || {};
  const out: OperationItem[] = [];

  for (const [path, methods] of Object.entries(paths)) {
    for (const [method, operation] of Object.entries(methods as any)) {
      const m = method.toUpperCase();
      if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(m)) continue;
      const tags = Array.isArray((operation as any).tags) ? (operation as any).tags : [];
      out.push({
        id: `${m} ${path}`,
        method: m,
        path,
        summary: (operation as any).summary,
        tags,
        operation,
      });
    }
  }

  return out.sort((a, b) => (a.path + a.method).localeCompare(b.path + b.method));
};

const stringify = (v: any) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const OpenApiExplorerPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [bodyText, setBodyText] = useState<string>('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setSpecError(null);
    setSpec(null);

    centralDbRequest({ method: 'GET', path: '/openapi', token: accessToken, signal: abort.signal })
      .then(res => {
        if (!res.ok) {
          throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
        }
        setSpec(res.data);
      })
      .catch(err => {
        if (String(err?.name || '') === 'AbortError') return;
        setSpecError(err?.message || 'Failed to load OpenAPI');
      });

    return () => abort.abort();
  }, [accessToken]);

  const operations = useMemo(() => (spec ? listOperations(spec) : []), [spec]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return operations;
    return operations.filter(op => {
      const hay = `${op.method} ${op.path} ${(op.summary || '')} ${(op.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [operations, search]);

  const selected = useMemo(() => filtered.find(o => o.id === selectedId) || operations.find(o => o.id === selectedId) || null, [filtered, operations, selectedId]);

  useEffect(() => {
    if (!selected) return;
    const opParams: any[] = Array.isArray(selected.operation?.parameters) ? selected.operation.parameters : [];
    const next: Record<string, string> = {};
    for (const p of opParams) {
      if (p?.in === 'path' || p?.in === 'query') {
        next[p.name] = '';
      }
    }
    setParams(next);

    const schema = extractJsonSchema(selected.operation);
    if (schema) {
      setBodyText(stringify(schemaToExample(schema)));
    } else {
      setBodyText('');
    }
    setSendError(null);
    setResponse(null);
  }, [selectedId, selected]);

  const buildPath = (template: string): string => {
    let out = template;
    for (const [k, v] of Object.entries(params)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), encodeURIComponent(String(v)));
    }
    return out;
  };

  const send = async () => {
    if (!selected) return;
    setIsSending(true);
    setSendError(null);
    setResponse(null);

    try {
      const opParams: any[] = Array.isArray(selected.operation?.parameters) ? selected.operation.parameters : [];
      const query: Record<string, string> = {};
      for (const p of opParams) {
        if (p?.in !== 'query') continue;
        const v = params[p.name];
        if (v === undefined || v === '') continue;
        query[p.name] = v;
      }

      const pathParams = opParams.filter(p => p?.in === 'path');
      for (const p of pathParams) {
        if (p.required && !params[p.name]) {
          throw new Error(`Missing required path param: ${p.name}`);
        }
      }

      let body: any = undefined;
      const schema = extractJsonSchema(selected.operation);
      if (schema) {
        const parsed = safeJsonParse(bodyText || '');
        if (parsed.ok === false) throw new Error(parsed.error);
        body = parsed.data;
      }

      const res = await centralDbRequest({
        method: selected.method,
        path: buildPath(selected.path),
        query,
        body,
        token: accessToken,
      });

      setResponse(res);
      if (!res.ok) {
        setSendError(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setSendError(e?.message || 'Request failed');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" fontWeight={800}>
        API Explorer (auto-discovers new endpoints)
      </Typography>

      {specError ? <Alert severity="error">{specError}</Alert> : null}
      {!spec && !specError ? <Alert severity="info">Loading OpenAPI spec…</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5} lg={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <TextField label="Search" value={search} onChange={e => setSearch(e.target.value)} fullWidth />
              <Divider />
              <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                <List dense disablePadding>
                  {filtered.map(op => (
                    <ListItemButton key={op.id} selected={op.id === selectedId} onClick={() => setSelectedId(op.id)}>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={op.method} color={METHOD_COLORS[op.method] || 'default'} />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {op.path}
                            </Typography>
                          </Stack>
                        }
                        secondary={op.summary || (op.tags?.length ? op.tags.join(', ') : '')}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filtered.length} operations
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7} lg={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {!selected ? (
              <Alert severity="info">Select an operation from the list.</Alert>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={selected.method} color={METHOD_COLORS[selected.method] || 'default'} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selected.path}
                  </Typography>
                </Stack>
                {selected.summary ? <Typography variant="subtitle1">{selected.summary}</Typography> : null}

                {(Array.isArray(selected.operation?.parameters) ? selected.operation.parameters : []).filter((p: any) => p?.in === 'path' || p?.in === 'query')
                  .length ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Parameters
                    </Typography>
                    <Grid container spacing={1.5}>
                      {(selected.operation.parameters as any[])
                        .filter(p => p?.in === 'path' || p?.in === 'query')
                        .map(p => (
                          <Grid key={`${p.in}:${p.name}`} item xs={12} md={6}>
                            <TextField
                              label={`${p.name} (${p.in})${p.required ? ' *' : ''}`}
                              value={params[p.name] || ''}
                              onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Stack>
                ) : null}

                {extractJsonSchema(selected.operation) ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      JSON body
                    </Typography>
                    <TextField
                      value={bodyText}
                      onChange={e => setBodyText(e.target.value)}
                      fullWidth
                      multiline
                      minRows={8}
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const parsed = safeJsonParse(bodyText || '');
                          if (parsed.ok === false) {
                            setSendError(parsed.error);
                            return;
                          }
                          setBodyText(stringify(parsed.data));
                        }}
                      >
                        Format
                      </Button>
                    </Stack>
                  </Stack>
                ) : null}

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={send} disabled={isSending}>
                    Send
                  </Button>
                </Stack>

                {sendError ? <Alert severity="error">{sendError}</Alert> : null}
                {response ? (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      HTTP {response.status}
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{stringify(response.data)}</pre>
                  </Paper>
                ) : null}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default OpenApiExplorerPage;
